/**
 * @fileoverview KOL profile page route
 * @module routes/kols/$kolSlug
 */

import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { KOLLeftSection } from '@/features/kols/components/KOLLeftSection';
import { KOLRightSection } from '@/features/kols/components/KOLRightSection';
import { KOLPromotedProjectCardWithBlockchain } from '@/features/kols/components/KOLPromotedProjectCardWithBlockchain';
import { usePublicKOLProfile } from '@/features/kols/hooks/usePublicKOLProfile';
import { useKOLPromotedProjects } from '@/features/kols/hooks/useKOLPromotedProjects';

/**
 * KOL profile page component.
 *
 * Displays detailed information about a specific KOL (Key Opinion Leader).
 * Route uses SEO-friendly slug format following industry standards.
 *
 * Uses split layout identical to deals page:
 * - Left section (446px): Fixed width with independent scrolling
 * - 36px gap between sections
 * - Right section (738px): Flexible width with independent scrolling
 *
 * Features:
 * - KOL profile information display
 * - Campaign history and statistics
 * - Platform presence and engagement metrics
 * - Contact/hire functionality
 *
 * Follows SOLID principles:
 * - Single Responsibility: Page composition only
 * - Open/Closed: Extensible through component props
 * - Dependency Inversion: Components injected through imports
 *
 * SEO Benefits:
 * - Slug-based URLs improve search engine rankings
 * - Readable URLs enhance user experience and shareability
 * - KOLs can brand their personal vanity URLs
 *
 * @component
 * @example
 * ```tsx
 * // Accessed via routes like:
 * // /kols/crypto-expert-john
 * // /kols/defi-influencer-sarah
 * // /kols/nft-collector-mike
 * ```
 */
function KOLProfilePage(): ReactElement {
  const { kolSlug } = Route.useParams();

  // Fetch KOL profile data using React Query hook
  const { data: kol, isLoading, error } = usePublicKOLProfile(kolSlug);


  // Fetch promoted projects (Option A: KOL-specific projects)
  const {
    data: promotedProjects,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useKOLPromotedProjects(kol?.id ?? '', {
    pageSize: 4,
    enabled: Boolean(kol?.id), // Only fetch when KOL data is loaded
  });


  // Loading state
  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#44FF02]"></div>
          <p className="mt-4 text-white text-sm">Loading KOL profile...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="relative min-h-screen bg-background overflow-hidden flex items-center justify-center" style={{ paddingTop: '80px' }}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">Error Loading KOL Profile</h1>
          <p className="text-gray-400">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-background overflow-hidden" style={{ paddingTop: '80px' }}>
      {/* Split layout container - Mobile/Tablet: vertical stack, Desktop: horizontal split */}
      <div className="flex flex-col lg:flex-row w-full max-w-[1400px] mx-auto px-4">
        {/* Left Section - Mobile/Tablet: full width, Desktop (≥1024px): fixed 446px */}
        <KOLLeftSection kol={kol} isLoading={isLoading} />

        {/* Gap between sections - Mobile: 0px, Tablet: 24px, Desktop: 36px */}
        <div className="h-0 lg:h-auto lg:w-6 xl:w-9 flex-shrink-0" />

        {/* Right Section - Mobile/Tablet: full width, Desktop: flexible width */}
        <KOLRightSection kol={kol} isLoading={isLoading} />
      </div>

      {/* Full Width Section Below */}
      <div className="w-full max-w-[1400px] mx-auto px-4">
        {/* Horizontal Separator */}
        <div className="w-full h-px bg-[#282828]" />

        {/* Currently Promoting Section */}
        <div className="w-full">
          {/* Section Title with Vertical Lines */}
          <div className="flex items-center justify-center gap-2 md:gap-4">
            {/* Left Vertical Line - Hidden on mobile */}
            <div className="hidden md:block w-px bg-[#282828]" style={{ height: '64px' }} />

            {/* Title - Responsive font size */}
            <h2
              className="text-center text-[18px] md:text-[20px]"
              style={{
                color: '#6F6BFF',
                fontFamily: '"Nunito Sans"',
                fontStyle: 'normal',
                fontWeight: 700,
              }}
            >
              Currently Promoting on Onicorn
            </h2>

            {/* Right Vertical Line - Hidden on mobile */}
            <div className="hidden md:block w-px bg-[#282828]" style={{ height: '64px' }} />
          </div>

          {/* Horizontal Separator */}
          <div className="w-full h-px bg-[#282828] mb-6 md:mb-8" />

          {/* Cards Grid - Responsive: 1 col mobile, 2 cols tablet, 4 cols desktop */}
          {isLoadingProjects ? (
            // Loading state - show 4 skeleton cards
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(4)].map((_, index) => (
                <div
                  key={index}
                  className="rounded-[6px] p-[1px] bg-[linear-gradient(135deg,transparent_50%,#8E8E8E_50%,#282828_100%),linear-gradient(315deg,transparent_50%,#8E8E8E_50%,#282828_100%)] h-fit"
                >
                  <div className="w-full h-full rounded-[5px] bg-[#121212] shadow-[0_0_9.4px_0_rgba(255,255,255,0.25)_inset] overflow-hidden">
                    <div className="animate-pulse">
                      <div className="w-full h-[100px] bg-gray-700" />
                      <div className="p-4 space-y-3">
                        <div className="h-4 bg-gray-700 rounded w-3/4" />
                        <div className="h-3 bg-gray-700 rounded w-full" />
                        <div className="h-2 bg-gray-700 rounded w-full" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : projectsError ? (
            // Error state
            <div className="text-center py-8">
              <p className="text-red-400">Failed to load promoted projects</p>
              <p className="text-gray-500 text-sm mt-2">{projectsError.message}</p>
            </div>
          ) : !promotedProjects || promotedProjects.length === 0 ? (
            // Empty state
            <div className="text-center py-8">
              <p className="text-gray-400">No active projects to display</p>
            </div>
          ) : (
            // Success state - render real project cards
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {promotedProjects.map((projectProps, index) => (
                <div
                  key={index}
                  className="rounded-[6px] p-[1px] bg-[linear-gradient(135deg,transparent_50%,#8E8E8E_50%,#282828_100%),linear-gradient(315deg,transparent_50%,#8E8E8E_50%,#282828_100%)] h-fit"
                >
                  <div className="w-full h-full rounded-[5px] bg-[#121212] shadow-[0_0_9.4px_0_rgba(255,255,255,0.25)_inset] overflow-hidden">
                    <KOLPromotedProjectCardWithBlockchain {...projectProps} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/kols/$kolSlug')({
  component: KOLProfilePage,
});
